import { platform } from './index';

export const PRODUCTION_API_ORIGIN = 'https://api.zodiacus.app';

function cleanOrigin(value) {
  return String(value || '').trim().replace(/\/+$/, '');
}

function parseAbsoluteOrigin(value, { httpsOnly = false, rejectLoopback = false, label = 'endpoint' } = {}) {
  const cleaned = cleanOrigin(value);
  if (!cleaned) throw new Error(`${label}_missing`);

  let parsed;
  try {
    parsed = new URL(cleaned);
  } catch {
    throw new Error(`${label}_malformed`);
  }

  if (parsed.origin !== cleaned || parsed.pathname !== '/' || parsed.search || parsed.hash) {
    throw new Error(`${label}_must_be_origin`);
  }
  if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error(`${label}_invalid_protocol`);
  if (httpsOnly && parsed.protocol !== 'https:') throw new Error(`${label}_https_required`);
  if (rejectLoopback && /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/i.test(parsed.hostname)) {
    throw new Error(`${label}_loopback_forbidden`);
  }
  return parsed.origin;
}

function browserLocation(locationLike) {
  if (locationLike) return locationLike;
  if (typeof window !== 'undefined') return window.location;
  return { protocol: 'https:', hostname: 'zodiacus.app' };
}

export function resolveEndpoints({
  platformInfo = platform,
  env = process.env,
  location: locationLike,
} = {}) {
  const location = browserLocation(locationLike);
  const hostname = String(location.hostname || '').toLowerCase();
  const isLocalWeb = /^(localhost|127\.0\.0\.1)$/.test(hostname);

  if (platformInfo?.isNative) {
    const androidProfile = String(env.REACT_APP_ANDROID_ENV || '').trim().toLowerCase();
    if (!['development', 'production'].includes(androidProfile)) {
      throw new Error('android_environment_required');
    }

    if (androidProfile === 'production') {
      const restOrigin = parseAbsoluteOrigin(
        env.REACT_APP_ANDROID_API_URL,
        { httpsOnly: true, rejectLoopback: true, label: 'android_rest_endpoint' }
      );
      const socketOrigin = parseAbsoluteOrigin(
        env.REACT_APP_ANDROID_SOCKET_URL,
        { httpsOnly: true, rejectLoopback: true, label: 'android_socket_endpoint' }
      );
      return Object.freeze({ profile: 'production-android', restOrigin, socketOrigin });
    }

    const restOrigin = parseAbsoluteOrigin(env.REACT_APP_ANDROID_API_URL, {
      rejectLoopback: true,
      label: 'android_rest_endpoint',
    });
    const socketOrigin = parseAbsoluteOrigin(env.REACT_APP_ANDROID_SOCKET_URL, {
      rejectLoopback: true,
      label: 'android_socket_endpoint',
    });
    return Object.freeze({ profile: 'development-android', restOrigin, socketOrigin });
  }

  const configuredRest = cleanOrigin(env.REACT_APP_API_URL || env.REACT_APP_API_BASE_URL || env.REACT_APP_BACKEND_URL);
  const configuredSocket = cleanOrigin(env.REACT_APP_SOCKET_URL);
  const isProductionWeb = String(env.NODE_ENV || '').toLowerCase() === 'production';

  if (!isProductionWeb && isLocalWeb) {
    const restOrigin = configuredRest || 'http://localhost:5001';
    return Object.freeze({
      profile: 'development-web',
      restOrigin,
      socketOrigin: configuredSocket || configuredRest || 'http://localhost:5001',
    });
  }

  const isZodiacusWeb = /(^|\.)zodiacus\.app$/.test(hostname);
  return Object.freeze({
    profile: 'production-web',
    // Preserve the Vercel /api rewrite contract on the canonical web origin.
    restOrigin: configuredRest || (isZodiacusWeb ? '' : PRODUCTION_API_ORIGIN),
    socketOrigin: configuredSocket || configuredRest || PRODUCTION_API_ORIGIN,
  });
}

export const endpoints = resolveEndpoints();
export const REST_API_ORIGIN = endpoints.restOrigin;
export const SOCKET_IO_ORIGIN = endpoints.socketOrigin;
